import AddButtonRounded from '@Components/AddButtonRounded';
import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import { IInfoCategoryMasterData } from '@Domains/MasterData';
import { Theme } from '@emotion/react';
import { DeleteOutline } from '@mui/icons-material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { Box, SxProps } from '@mui/system';
import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

const disabledFormInputStyleOther = (
  isCompleted: boolean,
  disabled: boolean
): SxProps<Theme> => ({
  '& .MuiInputBase-root': {
    backgroundColor: isCompleted || disabled ? 'grey.200' : 'white',
    '&.Mui-disabled': {
      backgroundColor: 'grey.200',
      color: 'text.primary',
      WebkitTextFillColor: 'unset',
    },
  },
  pointerEvents: isCompleted ? 'none' : undefined,
});

type InputOtherSectionProps = {
  title: string;
  name: string;
  isCompletedTh?: boolean;
  isCompletedEn?: boolean;
};

const InputOtherSection = ({
  title,
  name,
  isCompletedTh,
  isCompletedEn,
}: InputOtherSectionProps) => {
  const { control, getValues, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });
  const stakeholderType = name.startsWith('other')
    ? name.slice(5).charAt(0).toLowerCase() + name.slice(6)
    : name;
  const values = getValues(stakeholderType);
  const disabled = values?.some(
    (item: IInfoCategoryMasterData) => item.nameEn === 'Others'
  );

  useEffect(() => {
    if (!disabled) {
      if (fields.length > 0) {
        for (let i = fields.length - 1; i >= 0; i--) {
          remove(i);
        }
      }
      setValue(name, [{ nameTh: '', nameEn: '' }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  useEffect(() => {
    const currentValue = getValues(name);
    if (
      !currentValue ||
      (Array.isArray(currentValue) && currentValue.length === 0)
    ) {
      append({ nameTh: '', nameEn: '' });
    }
  }, [append, getValues, name]);

  return (
    <Box>
      <Flex alignItems={'center'} my={1}>
        <Typography>{title}</Typography>
        <Tooltip
          placement="top"
          sx={{ whiteSpace: 'pre-wrap' }}
          title={
            <>
              หากต้องการเพิ่มตัวเลือกอื่น ๆ กรุณาเลือก "อื่น ๆ"
              <br />
              ในรายการด้านบนเพื่อเพิ่มข้อมูล
            </>
          }
        >
          <IconButton size="small" sx={{ mt: 0.5 }}>
            <HelpOutlineOutlinedIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      </Flex>
      {fields.map((field, index) => (
        <Flex
          key={field.id}
          bgcolor={'grey.100'}
          p={1}
          mb={1}
          borderRadius={1}
          width={1}
          alignItems={'center'}
        >
          <Flex flexDirection={'column'} gap={1} width={1}>
            <FormInput
              name={`${name}.${index}.nameTh`}
              control={control}
              fullWidth
              placeholder="กรุณาระบุอื่นๆ (ภาษาไทย)"
              disabled={!disabled}
              slotProps={{
                input: {
                  readOnly: isCompletedTh,
                  tabIndex: isCompletedTh ? -1 : undefined,
                },
              }}
              sx={disabledFormInputStyleOther(
                isCompletedTh as boolean,
                !disabled
              )}
            />
            <FormInput
              name={`${name}.${index}.nameEn`}
              control={control}
              fullWidth
              placeholder="กรุณาระบุอื่นๆ (ภาษาอังกฤษ)"
              disabled={!disabled}
              slotProps={{
                input: {
                  readOnly: isCompletedEn,
                  tabIndex: isCompletedEn ? -1 : undefined,
                },
              }}
              sx={disabledFormInputStyleOther(
                isCompletedEn as boolean,
                !disabled
              )}
            />
          </Flex>
          {fields.length > 1 && !isCompletedTh && !isCompletedEn && (
            <Box ml={2}>
              <DeleteOutline
                fontSize="small"
                sx={{
                  cursor: 'pointer',
                  visibility:
                    isCompletedTh || isCompletedEn ? 'hidden' : 'visible',
                }}
                onClick={() => remove(index)}
              />
            </Box>
          )}
        </Flex>
      ))}
      <AddButtonRounded
        name="เพิ่มรายการอื่นๆ"
        onClick={() => append({ nameTh: '', nameEn: '' })}
        disabled={!disabled || isCompletedTh || isCompletedEn}
      />
    </Box>
  );
};

export default InputOtherSection;
